<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import inboxesApi from 'dashboard/api/inboxes.js';
import TemplateCreator from './templates/TemplateCreator.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

// 'list' | 'create' | 'edit' — inline view switching, no modal
const view = ref('list');
const selectedTemplate = ref(null);
const editingTemplate = ref(null);
const isSyncing = ref(false);

const whatsAppTemplates = computed(() => {
  const templates = store.getters['inboxes/getManageableWhatsAppTemplates'](
    props.inbox.id
  );
  return Array.isArray(templates) ? templates : [];
});

const hasTemplates = computed(() => whatsAppTemplates.value.length > 0);

const isWhatsAppChannel = computed(
  () => props.inbox.channel_type === 'Channel::Whatsapp'
);

function statusBadgeClass(status) {
  switch (status?.toLowerCase()) {
    case 'approved':
      return 'bg-n-teal-3 text-n-teal-11';
    case 'pending':
      return 'bg-n-amber-3 text-n-amber-11';
    case 'rejected':
      return 'bg-n-ruby-3 text-n-ruby-11';
    default:
      return 'bg-n-slate-3 text-n-slate-11';
  }
}

function statusText(status) {
  switch (status?.toLowerCase()) {
    case 'approved':
      return t('INBOX_MGMT.TEMPLATES.STATUS.APPROVED');
    case 'pending':
      return t('INBOX_MGMT.TEMPLATES.STATUS.PENDING');
    case 'rejected':
      return t('INBOX_MGMT.TEMPLATES.STATUS.REJECTED');
    default:
      return t('INBOX_MGMT.TEMPLATES.STATUS.UNKNOWN');
  }
}

function getTemplateBody(template) {
  return template.components?.find(c => c.type === 'BODY')?.text || '';
}

function getTemplateHeader(template) {
  return template.components?.find(c => c.type === 'HEADER') || null;
}

function getTemplateFooter(template) {
  return template.components?.find(c => c.type === 'FOOTER')?.text || null;
}

function getTemplateButtons(template) {
  return template.components?.find(c => c.type === 'BUTTONS')?.buttons || [];
}

const selectedHeader = computed(() =>
  selectedTemplate.value ? getTemplateHeader(selectedTemplate.value) : null
);
const selectedBody = computed(() =>
  selectedTemplate.value ? getTemplateBody(selectedTemplate.value) : ''
);
const selectedFooter = computed(() =>
  selectedTemplate.value ? getTemplateFooter(selectedTemplate.value) : null
);
const selectedButtons = computed(() =>
  selectedTemplate.value ? getTemplateButtons(selectedTemplate.value) : []
);

function buttonIcon(btn) {
  if (btn.type === 'URL') return 'link';
  if (btn.type === 'PHONE_NUMBER') return 'call';
  return 'arrow-reply';
}

function openDetail(template) {
  selectedTemplate.value = template;
}

function closeDetail() {
  selectedTemplate.value = null;
}

function openCreate() {
  selectedTemplate.value = null;
  editingTemplate.value = null;
  view.value = 'create';
}

function openEdit(template) {
  editingTemplate.value = template;
  selectedTemplate.value = null;
  view.value = 'edit';
}

function isTemplateEditable(template) {
  return ['APPROVED', 'REJECTED', 'PAUSED'].includes(
    template?.status?.toUpperCase()
  );
}

function backToList() {
  view.value = 'list';
  editingTemplate.value = null;
}

function onTemplatesSynced(templates) {
  store.dispatch('inboxes/setWhatsAppTemplates', {
    inboxId: props.inbox.id,
    templates,
  });
  editingTemplate.value = null;
  view.value = 'list';
}

async function syncTemplates() {
  if (!isWhatsAppChannel.value) return;
  isSyncing.value = true;
  try {
    const response = await inboxesApi.syncTemplates(props.inbox.id);
    store.dispatch('inboxes/setWhatsAppTemplates', {
      inboxId: props.inbox.id,
      templates: response.data.templates,
    });
    useAlert(t('INBOX_MGMT.TEMPLATES.SYNC_SUCCESS'));
  } catch (error) {
    const serverMessage =
      error?.response?.data?.error || error?.response?.data?.message;
    useAlert(serverMessage || t('INBOX_MGMT.TEMPLATES.SYNC_ERROR'));
  } finally {
    isSyncing.value = false;
  }
}
</script>

<template>
  <!-- ── EDIT VIEW ── -->
  <div v-if="view === 'edit'" class="flex flex-col h-[calc(100vh-160px)] -my-8">
    <TemplateCreator
      :inbox="inbox"
      :template="editingTemplate"
      :back-label="$t('INBOX_MGMT.TEMPLATES.TITLE')"
      @close="backToList"
      @back="backToList"
      @synced="onTemplatesSynced"
    />
  </div>

  <!-- ── CREATE VIEW ── fills the section row exactly; header is a static flex item, never scrolls -->
  <div
    v-else-if="view === 'create'"
    class="flex flex-col h-[calc(100vh-160px)] -my-8"
  >
    <TemplateCreator
      :inbox="inbox"
      :back-label="$t('INBOX_MGMT.TEMPLATES.TITLE')"
      @close="backToList"
      @back="backToList"
      @synced="onTemplatesSynced"
    />
  </div>

  <!-- ── LIST VIEW ── -->
  <div v-else class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.TEMPLATES.TITLE')"
      :sub-title="$t('INBOX_MGMT.TEMPLATES.SUBTITLE')"
    >
      <!-- Toolbar -->
      <div class="mb-5 flex items-center justify-between gap-4">
        <p class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.TEMPLATES.DESCRIPTION') }}
        </p>
        <div class="flex items-center gap-2 shrink-0">
          <NextButton
            v-if="isWhatsAppChannel"
            size="sm"
            ghost
            :loading="isSyncing"
            :label="
              isSyncing
                ? $t('INBOX_MGMT.TEMPLATES.SYNCING')
                : $t('INBOX_MGMT.TEMPLATES.SYNC_BUTTON')
            "
            @click="syncTemplates"
          />
          <NextButton
            size="sm"
            solid
            blue
            :label="$t('INBOX_MGMT.TEMPLATES.NEW_TEMPLATE_BUTTON')"
            @click="openCreate"
          />
        </div>
      </div>

      <!-- List + detail side-by-side when a template is selected -->
      <div class="flex gap-5">
        <!-- Template cards -->
        <div class="flex-1 min-w-0">
          <div v-if="hasTemplates" class="space-y-3">
            <div
              v-for="template in whatsAppTemplates"
              :key="template.id"
              class="rounded-xl border px-5 py-4 cursor-pointer transition-colors"
              :class="
                selectedTemplate?.id === template.id
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak bg-n-background hover:bg-n-slate-1'
              "
              @click="openDetail(template)"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2 mb-1">
                    <h3 class="text-sm font-semibold text-n-slate-12 truncate">
                      {{ template.name }}
                    </h3>
                    <span
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0"
                      :class="statusBadgeClass(template.status)"
                    >
                      {{ statusText(template.status) }}
                    </span>
                  </div>
                  <p
                    class="text-sm text-n-slate-11 line-clamp-2 leading-relaxed"
                  >
                    {{ getTemplateBody(template) }}
                  </p>
                </div>
                <div class="flex flex-col items-end gap-1.5 shrink-0">
                  <span
                    class="text-[11px] text-n-slate-9 bg-n-slate-3 px-2 py-0.5 rounded-full"
                  >
                    {{ template.language }}
                  </span>
                  <span class="text-[11px] text-n-slate-9">
                    {{ template.category }}
                  </span>
                  <button
                    v-if="isTemplateEditable(template)"
                    class="text-[11px] text-n-brand hover:underline mt-0.5"
                    @click.stop="openEdit(template)"
                  >
                    {{ $t('INBOX_MGMT.TEMPLATES.EDIT.EDIT_BUTTON') }}
                  </button>
                  <span
                    v-else
                    class="text-[11px] text-n-slate-8 mt-0.5"
                    :title="$t('INBOX_MGMT.TEMPLATES.EDIT.NOT_EDITABLE')"
                  >
                    {{ statusText(template.status) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-else
            class="flex flex-col items-center justify-center py-16 text-center"
          >
            <div
              class="w-12 h-12 rounded-xl bg-n-slate-3 flex items-center justify-center mb-4"
            >
              <fluent-icon
                icon="document-outline"
                class="text-n-slate-8"
                size="24"
              />
            </div>
            <h3 class="text-base font-semibold text-n-slate-12 mb-2">
              {{ $t('INBOX_MGMT.TEMPLATES.NO_TEMPLATES_TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11 max-w-sm">
              {{ $t('INBOX_MGMT.TEMPLATES.NO_TEMPLATES_DESCRIPTION') }}
            </p>
            <NextButton
              class="mt-6"
              size="sm"
              solid
              blue
              :label="$t('INBOX_MGMT.TEMPLATES.NEW_TEMPLATE_BUTTON')"
              @click="openCreate"
            />
          </div>
        </div>

        <!-- Detail panel — slides in inline when a template is selected -->
        <Transition
          enter-active-class="transition-[opacity,transform] duration-[180ms] ease-in-out"
          enter-from-class="opacity-0 translate-x-3"
          enter-to-class="opacity-100 translate-x-0"
          leave-active-class="transition-[opacity,transform] duration-[180ms] ease-in-out"
          leave-from-class="opacity-100 translate-x-0"
          leave-to-class="opacity-0 translate-x-3"
        >
          <div
            v-if="selectedTemplate"
            class="w-80 shrink-0 rounded-xl border border-n-weak bg-n-background overflow-hidden self-start sticky top-0"
          >
            <!-- Detail header -->
            <div
              class="flex items-start justify-between px-5 py-4 border-b border-n-weak"
            >
              <div class="min-w-0 flex-1">
                <h2 class="text-sm font-semibold text-n-slate-12 truncate">
                  {{ selectedTemplate.name }}
                </h2>
                <p class="text-xs text-n-slate-11 mt-0.5">
                  {{ $t('INBOX_MGMT.TEMPLATES.MODAL_TITLE') }}
                </p>
              </div>
              <div class="flex items-center gap-2 ml-3 shrink-0">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
                  :class="statusBadgeClass(selectedTemplate.status)"
                >
                  {{ statusText(selectedTemplate.status) }}
                </span>
                <button
                  v-if="isTemplateEditable(selectedTemplate)"
                  class="h-6 px-2 rounded-md text-[11px] font-medium text-n-brand border border-n-brand/30 hover:bg-n-brand/5 transition-colors shrink-0"
                  @click="openEdit(selectedTemplate)"
                >
                  {{ $t('INBOX_MGMT.TEMPLATES.EDIT.EDIT_BUTTON') }}
                </button>
                <button
                  class="w-6 h-6 rounded-md flex items-center justify-center text-n-slate-9 hover:text-n-slate-12 hover:bg-n-slate-3 transition-colors"
                  @click="closeDetail"
                >
                  <svg
                    class="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Detail body -->
            <div class="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              <!-- Header component -->
              <div v-if="selectedHeader">
                <p
                  class="text-[11px] font-semibold text-n-slate-9 uppercase tracking-wide mb-1.5"
                >
                  {{ $t('INBOX_MGMT.TEMPLATES.DETAIL_HEADER_LABEL') }}
                </p>
                <div class="bg-n-slate-3 rounded-lg px-3 py-2.5">
                  <p
                    v-if="selectedHeader.format === 'TEXT'"
                    class="text-sm font-semibold text-n-slate-12"
                  >
                    {{ selectedHeader.text }}
                  </p>
                  <p v-else class="text-sm text-n-slate-11 italic">
                    {{
                      $t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_MEDIA', {
                        type: selectedHeader.format,
                      })
                    }}
                  </p>
                </div>
              </div>

              <!-- Body -->
              <div>
                <p
                  class="text-[11px] font-semibold text-n-slate-9 uppercase tracking-wide mb-1.5"
                >
                  {{ $t('INBOX_MGMT.TEMPLATES.DETAIL_BODY_LABEL') }}
                </p>
                <div class="bg-n-slate-3 rounded-lg px-3 py-2.5">
                  <p
                    class="text-sm text-n-slate-12 whitespace-pre-line leading-relaxed"
                  >
                    {{ selectedBody }}
                  </p>
                </div>
              </div>

              <!-- Footer -->
              <div v-if="selectedFooter">
                <p
                  class="text-[11px] font-semibold text-n-slate-9 uppercase tracking-wide mb-1.5"
                >
                  {{ $t('INBOX_MGMT.TEMPLATES.DETAIL_FOOTER_LABEL') }}
                </p>
                <div class="bg-n-slate-3 rounded-lg px-3 py-2.5">
                  <p class="text-sm text-n-slate-11">
                    {{ selectedFooter }}
                  </p>
                </div>
              </div>

              <!-- Buttons -->
              <div v-if="selectedButtons.length">
                <p
                  class="text-[11px] font-semibold text-n-slate-9 uppercase tracking-wide mb-1.5"
                >
                  {{ $t('INBOX_MGMT.TEMPLATES.DETAIL_BUTTONS_LABEL') }}
                </p>
                <div class="space-y-1.5">
                  <div
                    v-for="(btn, i) in selectedButtons"
                    :key="i"
                    class="flex items-center gap-2.5 bg-n-slate-3 rounded-lg px-3 py-2"
                  >
                    <fluent-icon
                      :icon="buttonIcon(btn)"
                      class="text-n-slate-9 shrink-0"
                      size="13"
                    />
                    <span class="text-sm text-n-slate-12 font-medium truncate">
                      {{ btn.text }}
                    </span>
                    <span
                      v-if="btn.url || btn.phone_number"
                      class="text-xs text-n-slate-9 ml-auto truncate max-w-[100px]"
                    >
                      {{ btn.url || btn.phone_number }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Meta grid -->
              <div class="pt-3 border-t border-n-weak grid grid-cols-2 gap-3">
                <div>
                  <p
                    class="text-[11px] font-semibold text-n-slate-9 uppercase tracking-wide mb-0.5"
                  >
                    {{ $t('INBOX_MGMT.TEMPLATES.DETAIL_LANGUAGE_LABEL') }}
                  </p>
                  <p class="text-sm text-n-slate-12">
                    {{ selectedTemplate.language }}
                  </p>
                </div>
                <div>
                  <p
                    class="text-[11px] font-semibold text-n-slate-9 uppercase tracking-wide mb-0.5"
                  >
                    {{ $t('INBOX_MGMT.TEMPLATES.DETAIL_CATEGORY_LABEL') }}
                  </p>
                  <p class="text-sm text-n-slate-12">
                    {{ selectedTemplate.category }}
                  </p>
                </div>
                <div v-if="selectedTemplate.namespace" class="col-span-2">
                  <p
                    class="text-[11px] font-semibold text-n-slate-9 uppercase tracking-wide mb-0.5"
                  >
                    {{ $t('INBOX_MGMT.TEMPLATES.DETAIL_NAMESPACE_LABEL') }}
                  </p>
                  <p class="text-xs text-n-slate-11 font-mono break-all">
                    {{ selectedTemplate.namespace }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </SettingsSection>
  </div>
</template>
